import { useState } from 'react'
import type { DragEvent, MouseEvent } from 'react'
import type { PurchaseOrder } from '@/services/procurement'
import { money } from '@/services/procurement'
import { formatDateFull } from '@/lib/dateFormat'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { Badge } from '@/components/ui/Badge'
import {
  ARCHIVED_KANBAN_COLUMN,
  KANBAN_COLUMNS,
  type KanbanColumnKey,
  mapStatusToKanbanColumn,
  getCompletedStatusBadge,
  deriveDeliveryStatus,
  getReceivedProgressDisplay,
  isValidDropTransition,
  isPoDraggable,
} from './poWorkflow'
import { poTotal, PO_TOTAL_LABEL, poHasUnknownVat } from '@/features/proto/poListMetrics'

export interface PoKanbanViewProps {
  pos: PurchaseOrder[]
  suppliers: Array<{ id: string; name: string }>
  onSelectPo: (poId: string) => void
  onPoContextMenu?: (po: PurchaseOrder, e: MouseEvent) => void
  onTransitionToSent: (po: PurchaseOrder) => Promise<void>
  onTransitionPo?: (po: PurchaseOrder, targetCol: KanbanColumnKey) => Promise<void>
  /** Bật "Hiện đã lưu trữ": thêm cột LƯU TRỮ sau HOÀN THÀNH. */
  showArchived?: boolean
}

export function PoKanbanView({
  showArchived = false,
  pos,
  suppliers,
  onSelectPo,
  onPoContextMenu,
  onTransitionToSent,
  onTransitionPo,
}: PoKanbanViewProps) {
  const [draggedPo, setDraggedPo] = useState<PurchaseOrder | null>(null)
  const [dragOverCol, setDragOverCol] = useState<KanbanColumnKey | null>(null)
  const [transitioningPoId, setTransitioningPoId] = useState<string | null>(null)
  const [transitionError, setTransitionError] = useState<string | null>(null)

  function getSupplierName(supId: string): string {
    const found = suppliers.find((s) => s.id === supId)?.name
    return found || '—'
  }

  // Group POs into the 4 columns
  const columnData: Record<KanbanColumnKey, PurchaseOrder[]> = {
    DRAFT_GROUP: [],
    SENT: [],
    PARTIAL: [],
    COMPLETED: [],
    ARCHIVED: [],
  }
  const columns = showArchived ? [...KANBAN_COLUMNS, ARCHIVED_KANBAN_COLUMN] : KANBAN_COLUMNS

  for (const po of pos) {
    const col = po.archivedAt ? 'ARCHIVED' : mapStatusToKanbanColumn(po.status, po.lines)
    columnData[col].push(po)
  }

  // Drag handlers
  function handleDragStart(e: DragEvent<HTMLDivElement>, po: PurchaseOrder) {
    if (!isPoDraggable(po)) {
      e.preventDefault()
      return
    }
    setDraggedPo(po)
    setTransitionError(null)
    e.dataTransfer.setData('text/plain', po.id)
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragEnd() {
    setDraggedPo(null)
    setDragOverCol(null)
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>, colKey: KanbanColumnKey) {
    e.preventDefault()
    if (!draggedPo) return

    setDragOverCol(colKey)
    const currentCol = mapStatusToKanbanColumn(draggedPo.status, draggedPo.lines)
    const valid = isValidDropTransition(draggedPo.status, colKey, currentCol)
    e.dataTransfer.dropEffect = valid ? 'move' : 'none'
  }

  function handleDragLeave(colKey: KanbanColumnKey) {
    if (dragOverCol === colKey) {
      setDragOverCol(null)
    }
  }

  async function handleDrop(e: DragEvent<HTMLDivElement>, colKey: KanbanColumnKey) {
    e.preventDefault()
    setDragOverCol(null)
    if (!draggedPo) return

    const po = draggedPo
    setDraggedPo(null)

    const currentCol = mapStatusToKanbanColumn(po.status, po.lines)
    if (currentCol === colKey) {
      return
    }

    if (!isValidDropTransition(po.status, colKey, currentCol)) {
      setTransitionError(`Không thể kéo PO ${po.code} sang cột này.`)
      return
    }

    try {
      setTransitioningPoId(po.id)
      setTransitionError(null)
      if (onTransitionPo) {
        await onTransitionPo(po, colKey)
      } else if (colKey === 'SENT') {
        await onTransitionToSent(po)
      }
    } catch (err) {
      setTransitionError(err instanceof Error ? err.message : 'Có lỗi khi cập nhật trạng thái PO')
    } finally {
      setTransitioningPoId(null)
    }
  }

  return (
    <div className="space-y-2">
      {transitionError ? (
        <div className="rounded border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 flex items-center justify-between">
          <span>{transitionError}</span>
          <button
            type="button"
            onClick={() => setTransitionError(null)}
            className="text-red-500 hover:text-red-700 font-bold ml-2"
          >
            ✕
          </button>
        </div>
      ) : null}

      <div className="flex max-w-full gap-3 overflow-x-auto pb-4 pt-1">
        {columns.map((col) => {
          const cards = columnData[col.key]
          const isOver = dragOverCol === col.key
          const isValidTarget = draggedPo
            ? isValidDropTransition(
                draggedPo.status,
                col.key,
                mapStatusToKanbanColumn(draggedPo.status, draggedPo.lines),
              )
            : false

          let dropHighlightClass = ''
          if (isOver && draggedPo) {
            dropHighlightClass = isValidTarget
              ? 'drag-over-valid bg-emerald-50/80 border-emerald-400 ring-2 ring-emerald-300'
              : 'drag-over-invalid bg-rose-50/80 border-rose-400 ring-2 ring-rose-300'
          }

          return (
            <div
              key={col.key}
              onDragOver={(e) => handleDragOver(e, col.key)}
              onDragLeave={() => handleDragLeave(col.key)}
              onDrop={(e) => handleDrop(e, col.key)}
              className={`flex w-72 shrink-0 flex-col rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg)] transition-colors duration-150 ${dropHighlightClass}`}
            >
              {/* Column Header */}
              <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-slate-50/70 px-3 py-2.5 rounded-t-[var(--radius-md)]">
                <div>
                  <div className="text-xs font-bold text-slate-800 tracking-wide">
                    {col.title}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate max-w-[200px]" title={col.subtitle}>
                    {col.subtitle}
                  </div>
                </div>
                <span className="rounded-full bg-slate-200/80 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                  {cards.length}
                </span>
              </div>

              {/* Column Content */}
              <div className="flex min-h-[450px] max-h-[calc(100vh-280px)] flex-col gap-2.5 overflow-y-auto p-2.5">
                {cards.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center py-12 text-center text-xs text-slate-400 italic">
                    {isOver && isValidTarget ? 'Thả vào đây để gửi đơn' : 'Không có PO nào'}
                  </div>
                ) : (
                  cards.map((po) => {
                    const draggable = isPoDraggable(po)
                    const isTransitioning = transitioningPoId === po.id
                    const deliveryStatus = deriveDeliveryStatus(po.status, po.deliveryDate, po.lines)
                    const progress = getReceivedProgressDisplay(po)

                    return (
                      <div
                        key={po.id}
                        draggable={draggable && !isTransitioning}
                        onDragStart={(e) => handleDragStart(e, po)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onSelectPo(po.id)}
                        onContextMenu={(event) => {
                          if (!onPoContextMenu) return
                          event.preventDefault()
                          event.stopPropagation()
                          onPoContextMenu(po, event)
                        }}
                        onKeyDown={(event) => {
                          if (!onPoContextMenu || (event.key !== 'ContextMenu' && !(event.shiftKey && event.key === 'F10'))) return
                          event.preventDefault()
                          const rect = event.currentTarget.getBoundingClientRect()
                          onPoContextMenu(po, { clientX: rect.left + 32, clientY: rect.top + 24 } as MouseEvent)
                        }}
                        tabIndex={0}
                        className={`group relative rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-left shadow-[var(--shadow-level-1)] transition hover:border-[var(--color-primary)] hover:shadow-md cursor-pointer ${
                          !draggable ? 'cannot-drag opacity-90' : 'cursor-grab active:cursor-grabbing'
                        } ${po.archivedAt ? 'bg-slate-50/90 border-dashed border-slate-300 opacity-80' : ''}`}
                      >
                        {isTransitioning && (
                          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[var(--radius-md)] bg-white/80 backdrop-blur-2xs text-xs font-semibold text-emerald-800">
                            Đang xử lý chuyển đơn...
                          </div>
                        )}

                        {/* Top: PO Code & Badges */}
                        <div className="flex items-start justify-between gap-1.5">
                          <div className="text-xs font-bold text-slate-900 group-hover:text-blue-700 transition">
                            {po.code}
                          </div>

                          <div className="flex flex-wrap items-center justify-end gap-1">
                            {/* In COMPLETED column: Show required 3 distinct badges */}
                            {col.key === 'COMPLETED' ? (
                              (() => {
                                const b = getCompletedStatusBadge(po.status, po.lines)
                                return (
                                  <Badge tone={b.tone} className="text-[11px] font-medium py-0.5">
                                    {b.label}
                                  </Badge>
                                )
                              })()
                            ) : null}

                            {/* Delivery Status Badge */}
                            {col.key !== 'COMPLETED' || deliveryStatus.label !== getCompletedStatusBadge(po.status, po.lines).label ? (
                              <Badge tone={deliveryStatus.tone} className="text-[11px] font-medium py-0.5">
                                {deliveryStatus.label}
                              </Badge>
                            ) : null}
                          </div>
                        </div>

                        {/* Supplier */}
                        <div className="mt-1 text-xs text-slate-600 truncate font-medium" title={getSupplierName(po.supplierId)}>
                          {getSupplierName(po.supplierId)}
                        </div>

                        {/* Progress Bar & Absolute text */}
                        <div className="mt-2.5 space-y-1">
                          <div className="flex items-center justify-between text-[11px]">
                            <span
                              className="font-medium text-slate-700 truncate cursor-help"
                              title={progress.tooltip}
                            >
                              {progress.text}
                            </span>
                            <span className="tabular-nums font-semibold text-slate-500 ml-1 shrink-0">
                              {Math.round(progress.ratio * 100)}%
                            </span>
                          </div>

                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 border border-slate-200">
                            <div
                              className={`h-full transition-all duration-300 ${
                                progress.ratio >= 1
                                  ? 'bg-emerald-600'
                                  : progress.ratio > 0
                                  ? 'bg-blue-600'
                                  : 'bg-slate-300'
                              }`}
                              style={{ width: `${Math.min(progress.ratio * 100, 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Money Total */}
                        <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-[11px] text-slate-500">{PO_TOTAL_LABEL}:</span>
                          <span className="text-xs font-bold tabular-nums text-slate-900">
                            {money(poTotal(po))}
                          </span>
                        </div>

                        {/* Extra tags */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {po.deliveryDate ? (
                            <span className="text-[10px] text-slate-500">
                              Giao: {formatDateFull(po.deliveryDate)}
                            </span>
                          ) : null}

                          {poHasUnknownVat(po) ? (
                            <StatusBadge tone="warning" label="Chưa VAT" showDot={false} className="text-[10px] py-0" />
                          ) : null}

                          {po.editedAfterApprovalAt ? (
                            <Badge tone="warning" className="text-[10px] py-0 font-medium">
                              Sửa sau duyệt
                            </Badge>
                          ) : null}

                          {po.archivedAt ? (
                            <Badge tone="neutral" className="text-[10px] py-0 font-medium">
                              Đã lưu trữ
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
